import type { ReportRow } from '../../models/report.types'

const DAY_MS = 86_400_000
const NUMERIC_TIME_FIELDS = new Set(['day_of_week', 'day_of_month', 'day_of_year'])
const DAY_INDEX: Record<string, number> = {
  monday: 1,
  mon: 1,
  pazartesi: 1,
  tuesday: 2,
  tue: 2,
  sali: 2,
  wednesday: 3,
  wed: 3,
  carsamba: 3,
  thursday: 4,
  thu: 4,
  persembe: 4,
  friday: 5,
  fri: 5,
  cuma: 5,
  saturday: 6,
  sat: 6,
  cumartesi: 6,
  sunday: 7,
  sun: 7,
  pazar: 7,
}
const DAY_LABELS = [
  '',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]
const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const
const MONTH_INDEX: Record<string, number> = {
  january: 0,
  jan: 0,
  ocak: 0,
  february: 1,
  feb: 1,
  subat: 1,
  march: 2,
  mar: 2,
  mart: 2,
  april: 3,
  apr: 3,
  nisan: 3,
  may: 4,
  mayis: 4,
  june: 5,
  jun: 5,
  haziran: 5,
  july: 6,
  jul: 6,
  temmuz: 6,
  august: 7,
  aug: 7,
  agustos: 7,
  september: 8,
  sep: 8,
  eylul: 8,
  october: 9,
  oct: 9,
  ekim: 9,
  november: 10,
  nov: 10,
  kasim: 10,
  december: 11,
  dec: 11,
  aralik: 11,
}

function normalizedWord(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLocaleLowerCase('tr')
    .normalize('NFD')
    .replaceAll(/\p{Diacritic}/gu, '')
    .replaceAll('ı', 'i')
}

function isoWeekTimestamp(value: string): number | null {
  const match = /^(\d{4})-?W(\d{1,2})$/iu.exec(value.trim())
  if (!match) return null
  const year = Number(match[1])
  const week = Number(match[2])
  if (!Number.isInteger(year) || !Number.isInteger(week) || week < 1 || week > 53) return null
  const fourthOfJanuary = new Date(Date.UTC(year, 0, 4))
  const weekday = fourthOfJanuary.getUTCDay() || 7
  return fourthOfJanuary.getTime() - (weekday - 1) * DAY_MS + (week - 1) * 7 * DAY_MS
}

export function isNumericChartTimeField(field: string): boolean {
  return NUMERIC_TIME_FIELDS.has(field)
}

function numericDay(value: unknown): number | null {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

function weekDay(value: unknown): number | null {
  const numeric = numericDay(value)
  if (numeric !== null && numeric >= 1 && numeric <= 7) return numeric
  return DAY_INDEX[normalizedWord(value)] ?? null
}

function monthOfYear(value: unknown, row: ReportRow): number | null {
  const month = MONTH_INDEX[normalizedWord(value)] ?? Number(value) - 1
  if (!Number.isInteger(month) || month < 0 || month > 11) return null
  const year = Number(row.year)
  return Date.UTC(Number.isFinite(year) && year > 0 ? year : 2000, month, 1)
}

function calendarYear(value: unknown): number | null {
  const year = Number(value)
  return Number.isFinite(year) ? Date.UTC(year, 0, 1) : null
}

function calendarTimestamp(value: unknown, field: string): number | null {
  if (field === 'week' && typeof value === 'string') {
    const week = isoWeekTimestamp(value)
    if (week !== null) return week
  }
  const timestamp = new Date(String(value)).getTime()
  return Number.isFinite(timestamp) ? timestamp : null
}

/** Convert every report time bucket to the numeric x value Highcharts expects. */
export function normalizeChartTimeValue(
  value: unknown,
  field: string,
  row: ReportRow = {},
): number | null {
  if (value === null || value === undefined || value === '') return null
  switch (field) {
    case 'day_of_week':
      return weekDay(value)
    case 'day_of_month':
    case 'day_of_year':
      return numericDay(value)
    case 'month_of_year':
      return monthOfYear(value, row)
    case 'year':
      return calendarYear(value)
    default:
      return calendarTimestamp(value, field)
  }
}

export function formatChartTimeLabel(value: number, field: string): string {
  if (field === 'day_of_week') return DAY_LABELS[value] ?? String(value)
  if (field === 'day_of_month' || field === 'day_of_year') return String(value)
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  if (field === 'year') return String(date.getUTCFullYear())
  if (field === 'month' || field === 'month_of_year') {
    return `${MONTH_LABELS[date.getUTCMonth()]} ${date.getUTCFullYear()}`
  }
  return `${MONTH_LABELS[date.getUTCMonth()]} ${date.getUTCDate()}`
}

function nextTick(value: number, field: string): number {
  if (isNumericChartTimeField(field)) return value + 1
  if (field === 'month' || field === 'month_of_year') {
    const date = new Date(value)
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1)
  }
  if (field === 'year') return Date.UTC(new Date(value).getUTCFullYear() + 1, 0, 1)
  if (field === 'week') return value + 7 * DAY_MS
  return value + DAY_MS
}

export function buildChartTimeTicks(min: number, max: number, field: string): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) return []
  const ticks: number[] = []
  let current = min
  let guard = 0
  while (current <= max && guard < 2_000) {
    ticks.push(current)
    current = nextTick(current, field)
    guard += 1
  }
  if (ticks.at(-1) !== max) ticks.push(max)
  return [...new Set(ticks)].sort((left, right) => left - right)
}

export function sampleChartTimeTicks(ticks: readonly number[], maximum: number): number[] {
  if (maximum <= 0 || ticks.length <= maximum) return [...ticks]
  if (maximum === 1) return ticks.length ? [ticks.at(-1)!] : []
  const step = Math.ceil((ticks.length - 1) / (maximum - 1))
  return ticks.filter((_, index) => index === 0 || index === ticks.length - 1 || index % step === 0)
}

export function minimumChartTimeLabelWidth(field: string): number {
  if (field === 'month' || field === 'month_of_year') return 75
  if (field === 'week') return 104
  if (field === 'year') return 64
  return isNumericChartTimeField(field) ? 36 : 56
}

/** Accept both v3's nested breakdown response and v4's flat dimension response. */
export function expandChartBreakdownRows(rows: readonly ReportRow[]): ReportRow[] {
  return rows.flatMap((row) => {
    if (!Array.isArray(row.breakdown_values) || row.breakdown_values.length === 0) return [row]
    const { breakdown_values: children, ...parent } = row
    return children.map((child) => ({ ...parent, ...child }))
  })
}
